/**
 * STOP_CODING_AGENT action - Stop a running coding agent session
 *
 * Terminates an active PTY session. Use when the agent is done,
 * stuck, or needs to be cancelled.
 *
 * @module actions/stop-agent
 */

import {
  type Action,
  type ActionResult,
  type HandlerCallback,
  type HandlerOptions,
  type IAgentRuntime,
  logger,
  type Memory,
  type State,
} from "@elizaos/core";
import type { PTYService } from "../services/pty-service.js";

export const stopAgentAction: Action = {
  name: "STOP_CODING_AGENT",

  similes: [
    "KILL_CODING_AGENT",
    "TERMINATE_AGENT",
    "END_CODING_SESSION",
    "CANCEL_AGENT",
  ],

  description:
    "Stop a running coding agent session. " +
    "Terminates the PTY session and cleans up resources.",

  examples: [
    [
      {
        name: "{{user1}}",
        content: { text: "Stop the coding agent" },
      },
      {
        name: "{{agentName}}",
        content: {
          text: "I'll stop the coding session.",
          action: "STOP_CODING_AGENT",
        },
      },
    ],
    [
      {
        name: "{{user1}}",
        content: { text: "Kill the stuck agent" },
      },
      {
        name: "{{agentName}}",
        content: {
          text: "Terminating the coding agent.",
          action: "STOP_CODING_AGENT",
        },
      },
    ],
  ],

  validate: async (
    runtime: IAgentRuntime,
    _message: Memory,
  ): Promise<boolean> => {
    const ptyService = runtime.getService("PTY_SERVICE") as unknown as
      | PTYService
      | undefined;
    if (!ptyService) {
      return false;
    }
    try {
      const sessions = await Promise.race([
        ptyService.listSessions(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("validate timeout")), 2000),
        ),
      ]);
      return sessions.length > 0;
    } catch {
      return false;
    }
  },

  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state?: State,
    _options?: HandlerOptions,
    callback?: HandlerCallback,
  ): Promise<ActionResult | undefined> => {
    const ptyService = runtime.getService("PTY_SERVICE") as unknown as
      | PTYService
      | undefined;
    if (!ptyService) {
      if (callback) {
        await callback({
          text: "PTY Service is not available.",
        });
      }
      return { success: false, error: "SERVICE_UNAVAILABLE" };
    }

    const content = message.content as {
      sessionId?: string;
      all?: boolean;
    };

    // Stop all sessions if requested
    if (content.all) {
      const sessions = await ptyService.listSessions();
      if (sessions.length === 0) {
        if (callback) {
          await callback({
            text: "No active coding sessions to stop.",
          });
        }
        return { success: true, text: "No sessions to stop" };
      }

      for (const session of sessions) {
        try {
          await ptyService.stopSession(session.id);
        } catch (err) {
          logger.error(`Failed to stop session ${session.id}: ${err}`);
        }
      }

      // Clear state
      if (state?.codingSession) {
        delete state.codingSession;
      }

      if (callback) {
        await callback({
          text: `Stopped ${sessions.length} coding session(s).`,
        });
      }
      return {
        success: true,
        text: `Stopped ${sessions.length} sessions`,
        data: { stoppedCount: sessions.length },
      };
    }

    // Stop specific session
    let sessionId = content.sessionId;
    if (!sessionId && state?.codingSession) {
      sessionId = (state.codingSession as { id: string }).id;
    }

    if (!sessionId) {
      const sessions = await ptyService.listSessions();
      if (sessions.length === 0) {
        if (callback) {
          await callback({
            text: "No active coding sessions to stop.",
          });
        }
        return { success: true, text: "No sessions to stop" };
      }
      sessionId = sessions[sessions.length - 1].id;
    }

    const session = ptyService.getSession(sessionId);
    if (!session) {
      if (callback) {
        await callback({
          text: `Session ${sessionId} not found.`,
        });
      }
      return { success: false, error: "SESSION_NOT_FOUND" };
    }

    try {
      await ptyService.stopSession(sessionId);

      // Clear state if this was the current session
      if (
        state?.codingSession &&
        (state.codingSession as { id: string }).id === sessionId
      ) {
        delete state.codingSession;
      }

      if (callback) {
        await callback({
          text: `Stopped coding agent session ${sessionId}.`,
        });
      }
      return {
        success: true,
        text: `Stopped session ${sessionId}`,
        data: { sessionId, agentType: session.agentType },
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (callback) {
        await callback({
          text: `Failed to stop agent: ${errorMessage}`,
        });
      }
      return { success: false, error: errorMessage };
    }
  },

  parameters: [
    {
      name: "sessionId",
      description:
        "ID of the session to stop. If not specified, stops the current session.",
      required: false,
      schema: { type: "string" as const },
    },
    {
      name: "all",
      description: "If true, stop all active coding sessions.",
      required: false,
      schema: { type: "boolean" as const },
    },
  ],
};
